import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import type { Comment } from '../lib/types';

type CommentsSectionProps = {
  postId: string;
  initialComments: Comment[];
  turnstileSiteKey?: string;
};

type SubmissionState = 'idle' | 'submitting' | 'success' | 'error';

export function CommentsSection({ postId, initialComments, turnstileSiteKey }: CommentsSectionProps) {
  const [comments] = useState(initialComments);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [body, setBody] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaReady, setCaptchaReady] = useState<boolean>(!!turnstileSiteKey);
  const [status, setStatus] = useState<SubmissionState>('idle');
  const [error, setError] = useState<string | null>(null);
  const widgetContainerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  const commentCount = comments.length;

  const submissionDisabled = useMemo(() => {
    if (!turnstileSiteKey) return true;
    return !name.trim() || !body.trim() || !captchaToken || status === 'submitting';
  }, [body, captchaToken, name, status, turnstileSiteKey]);

  const resetCaptcha = () => {
    const turnstile = (window as any)?.turnstile;
    if (turnstile && widgetIdRef.current) {
      turnstile.reset(widgetIdRef.current);
      setCaptchaToken(null);
    }
  };

  useEffect(() => {
    if (!turnstileSiteKey) return;

    const renderWidget = () => {
      const turnstile = (window as any)?.turnstile;
      if (!turnstile || !widgetContainerRef.current) return;
      if (widgetIdRef.current) {
        try {
          turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore cleanup failures
        }
        widgetIdRef.current = null;
      }
      widgetContainerRef.current.innerHTML = '';
      widgetIdRef.current = turnstile.render(widgetContainerRef.current, {
        sitekey: turnstileSiteKey,
        callback: (token: string) => {
          setCaptchaToken(token);
          setCaptchaReady(true);
        },
        'expired-callback': () => setCaptchaToken(null),
        'error-callback': () => setCaptchaToken(null)
      });
    };

    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"]'
    );

    if (existingScript && (window as any)?.turnstile) {
      renderWidget();
      return () => undefined;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = () => renderWidget();
    script.onerror = () => setCaptchaReady(false);
    document.head.appendChild(script);

    return () => {
      if (widgetIdRef.current && (window as any)?.turnstile) {
        (window as any).turnstile.remove(widgetIdRef.current);
      }
      if (widgetContainerRef.current) {
        widgetContainerRef.current.innerHTML = '';
      }
    };
  }, [turnstileSiteKey]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setStatus('submitting');

    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${apiUrl}/api/v1/comments`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          postId,
          name: name.trim(),
          email: email.trim() || undefined,
          body: body.trim(),
          captchaToken
        })
      });

      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || 'Unable to post comment right now.');
      }

      setStatus('success');
      setName('');
      setEmail('');
      setBody('');
      setCaptchaToken(null);
      resetCaptcha();
    } catch (err: any) {
      setStatus('error');
      setError(err.message || 'Unexpected error');
      resetCaptcha();
    } finally {
      setStatus((prev) => (prev === 'submitting' ? 'idle' : prev));
    }
  }

  return (
    <section id="comments" className="comments-section">
      <div className="comments-header">
        <div>
          <div className="eyebrow">Comments</div>
          <h3>{commentCount === 1 ? '1 comment' : `${commentCount} comments`}</h3>
        </div>
        <p className="comments-note">
          Comments appear after they are approved. We check for spam and safety automatically.
        </p>
      </div>

      <div className="comments-layout">
        <div className="comments-list">
          {comments.length === 0 ? (
            <p className="placeholder">Be the first to share a thought.</p>
          ) : (
            comments.map((comment) => (
              <article key={comment.id} className="comment-card">
                <div className="comment-meta">
                  <span className="comment-author">{comment.name}</span>
                  {comment.created_at ? (
                    <>
                      <span className="dot-sep" />
                      <span className="comment-date">
                        {new Date(comment.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </>
                  ) : null}
                </div>
                <p className="comment-body">{comment.body}</p>
              </article>
            ))
          )}
        </div>

        <form className="comment-form" onSubmit={handleSubmit}>
          <div className="stack gap-12">
            <div className="form-field comment-field">
              <label htmlFor="comment-name">Name</label>
              <input
                id="comment-name"
                name="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required
              />
            </div>

            <div className="form-field comment-field">
              <label htmlFor="comment-email">
                Email <span className="muted">(optional)</span>
              </label>
              <input
                id="comment-email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>

            <div className="form-field comment-field">
              <label htmlFor="comment-body">Comment</label>
              <textarea
                id="comment-body"
                name="body"
                rows={4}
                maxLength={2000}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Keep it constructive and kind."
                required
              />
            </div>

            <div className="form-field comment-field">
              {!turnstileSiteKey ? (
                <p className="captcha-warning">
                  Add VITE_TURNSTILE_SITE_KEY to enable the captcha widget.
                </p>
              ) : (
                <div className="captcha-box" ref={widgetContainerRef}>
                  {!captchaReady ? <span className="muted">Loading captcha...</span> : null}
                </div>
              )}
            </div>

            {error ? <p className="form-error">{error}</p> : null}
            {status === 'success' ? (
              <p className="form-success">
                Thanks! Your comment was received and will appear once approved.
              </p>
            ) : null}

            <button
              type="submit"
              className="button"
              disabled={submissionDisabled || status === 'submitting'}
            >
              {status === 'submitting' ? 'Submitting...' : 'Post comment'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
